// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Link Resolver
/** Utility class for resolving links in markdown transformation */
export class MarkdownLinkResolver {
    constructor(documentationData, config, sdkInstance, designSystemId, versionId) {
        this.cachedDocumentationBaseUrl = null;
        this.documentationData = documentationData;
        this.config = config;
        this.sdkInstance = sdkInstance;
        this.designSystemId = designSystemId;
        this.versionId = versionId;
    }
    /**
     * Resolves a link based on external URL or internal documentation item ID
     * @param externalUrl - Direct external URL if available
     * @param documentationItemId - Internal documentation item ID if available
     * @param pageContext - Current page context for relative path resolution
     * @returns Promise<string> - Resolved URL
     */
    async resolveLink(externalUrl, documentationItemId, pageContext) {
        // If external URL is provided, use it directly
        if (externalUrl) {
            return externalUrl;
        }
        // If internal documentation item ID is provided, resolve it
        if (documentationItemId) {
            const destinationPage = this.documentationData.allPages.filter((p) => p.id === documentationItemId || p.persistentId === documentationItemId)[0];
            if (destinationPage) {
                // For deployed markdown conversion, use base URL + relativeUrl
                if (this.config.urlType === "deployed") {
                    const baseUrl = await this.getDocumentationBaseUrl();
                    if (baseUrl && destinationPage.relativeUrl) {
                        return `${baseUrl}/${destinationPage.relativeUrl}`;
                    }
                    // Fallback to relative path if no deployed URL available
                }
                return this.relativePathToPage(destinationPage, pageContext);
            }
        }
        // Fallback to anchor link
        return "#";
    }
    /**
     * Generates relative path from current page to target page
     * @param targetPage - Page to link to
     * @param currentPage - Current page context
     * @returns string - Relative path
     */
    relativePathToPage(targetPage, currentPage) {
        // Get page paths by splitting slugs
        const currentPagePath = currentPage.slug ? currentPage.slug.split("/") : [];
        const targetPagePath = targetPage.slug ? targetPage.slug.split("/") : [];
        // Remove empty segments
        const currentPathSegments = currentPagePath.filter((segment) => segment.length > 0);
        const targetPathSegments = targetPagePath.filter((segment) => segment.length > 0);
        // Find common ancestor
        let commonAncestorIndex = 0;
        const minLength = Math.min(currentPathSegments.length, targetPathSegments.length);
        for (let i = 0; i < minLength; i += 1) {
            if (currentPathSegments[i] === targetPathSegments[i]) {
                commonAncestorIndex = i + 1;
            }
            else {
                break;
            }
        }
        // Build relative path
        const upwardSteps = currentPathSegments.length - commonAncestorIndex;
        const downwardSteps = targetPathSegments.slice(commonAncestorIndex);
        let relativePath = "";
        // Add upward navigation (..)
        for (let i = 0; i < upwardSteps; i += 1) {
            relativePath += "../";
        }
        // Add downward navigation
        if (downwardSteps.length > 0) {
            relativePath += downwardSteps.join("/");
        }
        // If same level, link to current directory
        if (relativePath === "") {
            relativePath = "./";
        }
        // Add markdown file extension
        if (!relativePath.endsWith("/")) {
            relativePath += ".md";
        }
        else {
            relativePath += "index.md";
        }
        return relativePath;
    }
    /**
     * Retrieves the base documentation URL for deployed documentation
     * Uses caching to avoid repeated API calls
     * @returns string | null - Base documentation URL or null if not available
     */
    async getDocumentationBaseUrl() {
        if (this.cachedDocumentationBaseUrl === null) {
            try {
                this.cachedDocumentationBaseUrl =
                    await this.sdkInstance.documentation.getDocumentationUrl({
                        designSystemId: this.designSystemId,
                        versionId: this.versionId,
                    });
            }
            catch (error) {
                console.warn("Failed to fetch documentation base URL:", error);
                this.cachedDocumentationBaseUrl = null;
            }
        }
        return this.cachedDocumentationBaseUrl;
    }
}
